import React, { useEffect, useState } from "react";
import { Wallet, CalendarCheck, Globe2 } from "lucide-react";
import { api, fmt } from "../../lib/game";

export const COUNTRY_KEY = "burgeon_country";

export const CountryPicker = ({ countries, value, onChange }) => (
  <div data-testid="country-picker" className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
    {countries.map((c) => {
      const active = value === c.code;
      return (
        <button
          key={c.code}
          type="button"
          data-testid={`country-${c.code}`}
          onClick={() => onChange(c.code)}
          className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${
            active
              ? "border-emerald-500/60 bg-emerald-500/10"
              : "border-slate-700 bg-slate-950/40 hover:border-slate-600"
          }`}
        >
          <span className={`font-mono text-[11px] tracking-widest ${active ? "text-emerald-300" : "text-slate-500"}`}>
            {c.flag}
          </span>
          <p className="text-sm font-semibold text-slate-100 leading-tight">{c.name}</p>
        </button>
      );
    })}
  </div>
);

const Progress = ({ w }) => {
  const pct = Math.min(100, Math.round((w.days_played / Math.max(1, w.required_days)) * 100));
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <CalendarCheck className="w-3.5 h-3.5 text-teal-400" /> Salary progress this month
        </span>
        <span data-testid="salary-progress-text" className="font-mono text-slate-300">
          {w.days_played}/{w.required_days} play days
        </span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1.5 text-[11px] text-slate-500">
        {w.on_track
          ? `On track — ${fmt(w.salary_amount)} salary lands at the start of next month.`
          : `Play on about 2 days a week to earn a ${fmt(w.salary_amount)} salary next month.`}
      </p>
    </div>
  );
};

export const WalletCard = ({ user, country, startAmount, onSignIn }) => {
  const [w, setW] = useState(null);
  useEffect(() => {
    if (!user) return setW(null);
    api.get("/me/wallet", { params: { country } }).then(({ data }) => setW(data)).catch(() => setW(null));
  }, [user, country]);

  if (!user) {
    return (
      <div data-testid="guest-start-info" className="mt-4 rounded-2xl border border-slate-700 bg-slate-950/40 p-4 flex items-start gap-3">
        <Globe2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300">
          You start with <span data-testid="start-amount" className="font-mono font-semibold text-emerald-300">{fmt(startAmount)}</span>.
          Guest games are one-off.{" "}
          <button data-testid="wallet-signin-link" onClick={onSignIn} className="text-emerald-400 font-semibold hover:underline">
            Sign in
          </button>{" "}
          to keep your balance between games and earn a monthly salary.
        </p>
      </div>
    );
  }
  if (!w) return null;
  return (
    <div data-testid="wallet-card" className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
          <Wallet className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">
            {w.is_new ? "Your starting wallet" : "Your wallet"}
          </p>
          <p data-testid="wallet-balance" className="font-mono text-2xl font-bold text-slate-50">{fmt(w.wallet)}</p>
        </div>
      </div>
      <Progress w={w} />
    </div>
  );
};
